import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import TestimonialCard from '@/components/testimonials/TestimonialCard';
import { Testimonial, fetchHomepageTestimonials } from '@/lib/testimonials';

/**
 * Homepage social-proof strip. Shows the reviews an admin FEATURED, falling
 * back to the newest approved ones. Renders NOTHING when there is nothing
 * approved yet, so the homepage never shows an empty "reviews" box or a
 * placeholder testimonial we made up.
 */
const HomeTestimonials: React.FC<{ limit?: number }> = ({ limit = 3 }) => {
  const { t, path } = useI18n();
  const [rows, setRows] = useState<Testimonial[]>([]);

  useEffect(() => {
    let alive = true;
    fetchHomepageTestimonials(limit).then((data) => {
      if (alive) setRows(data);
    });
    return () => {
      alive = false;
    };
  }, [limit]);

  if (!rows.length) return null;

  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">
              {t('testi.homeTitle')}
            </h2>
            <p className="mt-2 text-slate-500">{t('testi.homeSub')}</p>
          </div>
          <Link
            to={path('/testimonials')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-card"
          >
            {t('testi.readAll')} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((item) => (
            <TestimonialCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeTestimonials;
